import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdCheck } from 'react-icons/md';
import '../master/MasterData.css';

interface Vendor {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  vendorType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface ReceiptItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  orderedQty: number;
  receivedQty: number;
  rejectedQty: number;
  unitPrice: number;
}

interface GoodsReceiptData {
  receiptId: number;
  receiptNo: string;
  receiptDate: string;
  poNo: string;
  vendorCode: string;
  vendorName: string;
  receivedBy: string;
  warehouseLocation: string;
  totalItems: number;
  status: 'PENDING' | 'INSPECTING' | 'PARTIAL' | 'COMPLETED' | 'REJECTED';
  items: ReceiptItem[];
  remarks: string;
}

interface ReceiptModalProps {
  receipt: GoodsReceiptData | null;
  viewMode: boolean;
  vendors: Vendor[];
  onClose: () => void;
  onSave: (receipt: GoodsReceiptData) => void;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ receipt, viewMode, vendors, onClose, onSave }) => {
  const [formData, setFormData] = useState<GoodsReceiptData>(
    receipt || {
      receiptId: 0,
      receiptNo: '',
      receiptDate: new Date().toISOString().split('T')[0],
      poNo: '',
      vendorCode: '',
      vendorName: '',
      receivedBy: '',
      warehouseLocation: '',
      totalItems: 0,
      status: 'PENDING',
      items: [],
      remarks: ''
    }
  );

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      totalItems: prev.items.length
    }));
  }, [formData.items]);

  const handleVendorChange = (vendorCode: string) => {
    const selectedVendor = vendors.find(v => v.vendorCode === vendorCode);
    if (selectedVendor) {
      setFormData({
        ...formData,
        vendorCode: selectedVendor.vendorCode,
        vendorName: selectedVendor.vendorName
      });
    }
  };

  const handleAddItem = () => {
    const newItem: ReceiptItem = {
      itemId: Date.now(),
      itemCode: '',
      itemName: '',
      orderedQty: 0,
      receivedQty: 0,
      rejectedQty: 0,
      unitPrice: 0
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const handleRemoveItem = (itemId: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.itemId !== itemId)
    });
  };

  const handleItemChange = (itemId: number, field: keyof ReceiptItem, value: any) => {
    setFormData({
      ...formData,
      items: formData.items.map(item =>
        item.itemId === itemId ? { ...item, [field]: value } : item
      )
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Goods Receipt' : receipt ? 'Edit Goods Receipt' : 'Record New Goods Receipt'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Receipt No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.receiptNo}
                  onChange={(e) => setFormData({ ...formData, receiptNo: e.target.value })}
                  placeholder="GR-2025-XXXX"
                />
              </div>
              <div className="form-group">
                <label>Receipt Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.receiptDate}
                  onChange={(e) => setFormData({ ...formData, receiptDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>PO No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.poNo}
                  onChange={(e) => setFormData({ ...formData, poNo: e.target.value })}
                  placeholder="PO-2025-XXXX"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vendor Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.vendorCode}
                  onChange={(e) => handleVendorChange(e.target.value)}
                >
                  <option value="">Select Vendor</option>
                  {vendors.filter(v => v.isActive === 'Y').map(vendor => (
                    <option key={vendor.vendorId} value={vendor.vendorCode}>
                      {vendor.vendorName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="PENDING">Pending</option>
                  <option value="INSPECTING">Inspecting</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Received By *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.receivedBy}
                  onChange={(e) => setFormData({ ...formData, receivedBy: e.target.value })}
                  placeholder="e.g., John Warehouse"
                />
              </div>
              <div className="form-group">
                <label>Warehouse Location *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.warehouseLocation}
                  onChange={(e) => setFormData({ ...formData, warehouseLocation: e.target.value })}
                  placeholder="e.g., Warehouse A"
                />
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4>Items</h4>
                {!viewMode && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                    <MdAdd /> Add Item
                  </button>
                )}
              </div>

              <table className="item-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Ordered Qty</th>
                    <th>Received Qty</th>
                    <th>Rejected Qty</th>
                    <th>Unit Price</th>
                    {!viewMode && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item.itemId}>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(item.itemId, 'itemCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.itemId, 'itemName', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.orderedQty}
                          onChange={(e) => handleItemChange(item.itemId, 'orderedQty', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.receivedQty}
                          onChange={(e) => handleItemChange(item.itemId, 'receivedQty', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.rejectedQty}
                          onChange={(e) => handleItemChange(item.itemId, 'rejectedQty', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.itemId, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      {!viewMode && (
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleRemoveItem(item.itemId)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
                placeholder="Additional notes about this receipt..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {receipt ? 'Update' : 'Record'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const GoodsReceipt: React.FC = () => {
  const [receipts, setReceipts] = useState<GoodsReceiptData[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState<GoodsReceiptData | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch vendors from API
    setVendors([
      {
        vendorId: 1,
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        vendorType: 'MANUFACTURER',
        tin: '123-456-789-000',
        email: 'foam@suppliers.com',
        phone: '+63 2 1111 2222',
        city: 'Laguna',
        isActive: 'Y'
      },
      {
        vendorId: 2,
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        vendorType: 'DISTRIBUTOR',
        tin: '987-654-321-000',
        email: 'chem@trading.com',
        phone: '+63 2 3333 4444',
        city: 'Batangas',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch goods receipts from API
    setReceipts([
      {
        receiptId: 1,
        receiptNo: 'GR-2025-0001',
        receiptDate: '2025-02-15',
        poNo: 'PO-2025-0001',
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        receivedBy: 'John Warehouse',
        warehouseLocation: 'Warehouse A',
        totalItems: 5,
        status: 'COMPLETED',
        items: [],
        remarks: 'All items received in good condition'
      },
      {
        receiptId: 2,
        receiptNo: 'GR-2025-0002',
        receiptDate: '2025-02-20',
        poNo: 'PO-2025-0002',
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        receivedBy: 'Jane Inventory',
        warehouseLocation: 'Factory - Main Plant',
        totalItems: 3,
        status: 'INSPECTING',
        items: [],
        remarks: 'Quality inspection in progress'
      },
      {
        receiptId: 3,
        receiptNo: 'GR-2025-0003',
        receiptDate: '2025-02-22',
        poNo: 'PO-2025-0003',
        vendorCode: 'VEND003',
        vendorName: 'Packaging Materials Ltd.',
        receivedBy: 'Mike Receiving',
        warehouseLocation: 'Warehouse B',
        totalItems: 4,
        status: 'PARTIAL',
        items: [],
        remarks: 'Partial delivery - 2 items pending'
      }
    ]);
  }, []);

  const filteredReceipts = receipts.filter(receipt =>
    receipt.receiptNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    receipt.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    receipt.poNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingReceipt(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (receipt: GoodsReceiptData) => {
    setEditingReceipt(receipt);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (receipt: GoodsReceiptData) => {
    setEditingReceipt(receipt);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (receiptId: number) => {
    if (window.confirm('Are you sure you want to delete this goods receipt?')) {
      setReceipts(receipts.filter(r => r.receiptId !== receiptId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'badge-success';
      case 'INSPECTING': return 'badge-info';
      case 'PARTIAL': return 'badge-warning';
      case 'REJECTED': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Goods Receipt</h2>
            <p className="subtitle">Track and manage incoming inventory from vendors</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Record New Receipt
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search goods receipts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Receipt No</th>
                  <th>Receipt Date</th>
                  <th>PO No</th>
                  <th>Vendor</th>
                  <th>Received By</th>
                  <th>Location</th>
                  <th>Total Items</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReceipts.map(receipt => (
                  <tr key={receipt.receiptId}>
                    <td className="code">{receipt.receiptNo}</td>
                    <td>{new Date(receipt.receiptDate).toLocaleDateString('en-US')}</td>
                    <td className="code">{receipt.poNo}</td>
                    <td>{receipt.vendorName}</td>
                    <td>{receipt.receivedBy}</td>
                    <td>{receipt.warehouseLocation}</td>
                    <td style={{ textAlign: 'center' }}>{receipt.totalItems}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(receipt.status)}`}>
                        {receipt.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(receipt)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(receipt)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(receipt.receiptId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredReceipts.length === 0 && (
              <div className="no-data">
                <p>No goods receipts found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <ReceiptModal
          receipt={editingReceipt}
          viewMode={viewMode}
          vendors={vendors}
          onClose={() => setShowModal(false)}
          onSave={(receipt) => {
            if (editingReceipt) {
              setReceipts(receipts.map(r =>
                r.receiptId === receipt.receiptId ? receipt : r
              ));
            } else {
              setReceipts([...receipts, { ...receipt, receiptId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

export default GoodsReceipt;
